import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import { QRCodeSVG } from "qrcode.react";
import { Button, Card, Input, message } from "antd";
import type { InputRef } from "antd";
import logo from "../../assets/icons/vivo_inner.png";
import authService from "../../services/authService";

interface MFASetupProps {
  qrcodeUrl: string;
  setStep: (step: string) => void;
}

export default function MFASetup({ qrcodeUrl, setStep }: MFASetupProps) {
  const navigate = useNavigate();

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isValid, setIsValid] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const inputRefs = useRef<(InputRef | null)[]>([]);

  const showErrorMsg = (message: React.ReactNode) => {
    messageApi.open({
      type: "error",
      content: message,
      duration: 5,
    });
  };

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Move to next input if value is entered
    if (value !== "" && index < 5) {
      inputRefs.current[index + 1]?.focus?.();
    }

    // Check if all digits are filled
    setIsValid(newCode.every((digit) => digit !== ""));
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // Handle backspace
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus?.();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);

    if (!/^\d+$/.test(pastedData)) {
      message.error("Please paste numbers only");
      return;
    }

    const newCode = [...code];
    pastedData.split("").forEach((digit, index) => {
      if (index < 6) {
        newCode[index] = digit;
      }
    });
    setCode(newCode);
    setIsValid(newCode.every((digit) => digit !== ""));

    // Focus the next empty input or the last input
    const nextEmptyIndex = newCode.findIndex((digit) => digit === "");
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus?.();
    } else {
      inputRefs.current[5]?.focus?.();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isValid) {
      setupMFA();
    }
  };

  const setupMFA = async () => {
    const credentials = {
      code: code.join(""),
      // secret: details?.secret,
    };
    // console.log("Code sent");
    if (credentials) {
      try {
        const response = await authService.enableMfa(credentials);
        // console.log("response", response);
        if (response?.status === 201 || response?.status === 200) {
          // navigate("/login");
          // setStep("otp-verification")
          // }
          //  else {
          // let navigateTo = from;
          // if (
          //   import.meta.env.MODE === "inmune" &&
          //   !from.includes("NCT05318976")
          // ) {
          //   navigateTo = "/NCT05318976";
          // }

          // navigate(navigateTo, { replace: true });
          navigate("/");
        }
        if (response?.status === 401) {
          showErrorMsg(
            <div className="flex flex-col">
              <p className="text-[15px]">Code Not Accepted</p>
              <p>
                The code you entered is either incorrect or is from the wrong
                authenticator account.
              </p>
              <p>
                Check that you are using the latest authenticator account linked
                to your Vivo login, or contact admin support.
              </p>
            </div>,
          );
        }
      } catch (error) {
        console.error(error);
        showErrorMsg(
          <div className="flex flex-col">
            <p className="text-[15px]">Code Not Accepted</p>
            <p>
              The code you entered is either incorrect or is from the wrong
              authenticator account.
            </p>
            <p>
              Check that you are using the latest authenticator account linked
              to your Vivo login, or contact admin support.
            </p>
          </div>,
        );
      }
    }
  };

  return (
    <div>
      {contextHolder}
      <div className="flex flex-col justify-center items-center h-screen">
        <Card className="card-form" style={{ width: "530px" }}>
          <p className="text-[24px] font-bold">
            Set up multi-factor authentication
          </p>
          <ol
            className="text-[14px] text-[#556C75] mt-3 mb-[40px]"
            style={{ listStyleType: "decimal", paddingLeft: "20px" }}
          >
            <li>
              First, scan the QR code using an authenticator app (e.g., Google
              Authenticator or Microsoft Authenticator).
            </li>
            <li>
              <strong>If this is your first time setting up MFA</strong>, simply
              add the code shown in your authenticator app.
            </li>
            <li>
              <strong>If you have previously set up MFA</strong>, please delete
              any older version of this account from your authenticator app to
              avoid login issues, and then scan the QR code again.
            </li>
          </ol>
          <div className="flex flex-col justify-center items-center mb-7">
            <QRCodeSVG
              value={qrcodeUrl}
              imageSettings={{
                src: logo,
                height: 25,
                width: 40,
                excavate: false,
              }}
            />
          </div>
          <p className=" flex flex-col mb-4 items-center">
            Next, enter the 6-digit code generated by the authenticator.
          </p>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              {code.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => {
                    handleKeyDown(index, e);
                    handleKeyPress(e);
                  }}
                  onPaste={handlePaste}
                  style={{
                    width: "48px",
                    height: "48px",
                    textAlign: "center",
                    fontSize: "20px",
                  }}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col justify-center items-center mt-4">
            <Button
              type="primary"
              htmlType="submit"
              className="w-36 mt-2 rounded-sm shadow-none"
              disabled={!isValid}
              onClick={setupMFA}
            >
              Enable MFA
            </Button>
            <p className="flex flex-col items-center text-[40px] text-[#D5DADC]">
              |
            </p>
            <Button
              className="w-36 text-[#064C68] rounded-sm bg-[#D2EFF9] border-[#064C68]"
              onClick={() => {
                setStep("login");
              }}
            >
              Return to Login
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
